/**
 * i18next (>= 25) looks resources up under the canonical BCP 47 form of a language code
 * (Intl.getCanonicalLocales), e.g. 'de-FORMAL' → 'de-formal'. Resource bundles must be stored
 * under the same form, otherwise every key resolves to itself. Falls back to the code unchanged
 * where the Intl API is missing — exactly like i18next does.
 */
export const toResourceLanguageCode = (code: string): string => {
    try {
        return Intl.getCanonicalLocales(code)[0] ?? code;
    } catch (e) {
        return code;
    }
};

const LEGACY_PLURAL_SUFFIX = /_plural$/;
const LEGACY_PLURAL_REFERENCE = /(\$t\([^)]*?)_plural\)/g;

/**
 * Tenant translations (languageConfig.translations) may still use the i18next v3 plural format
 * (`key_plural`). i18next >= 21 only knows `key_one`/`key_other`, so legacy keys and `$t(…_plural)`
 * references are mapped to `_other` before merging. An explicit `_other` key wins over `_plural`.
 */
export const migrateLegacyPluralKeys = (translations: Record<string, any>): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(translations)) {
        const migratedValue =
            value && typeof value === 'object' && !Array.isArray(value)
                ? migrateLegacyPluralKeys(value)
                : typeof value === 'string'
                ? value.replace(LEGACY_PLURAL_REFERENCE, '$1_other)')
                : value;
        const migratedKey = key.replace(LEGACY_PLURAL_SUFFIX, '_other');
        if (migratedKey !== key && migratedKey in translations) continue;
        result[migratedKey] = migratedValue;
    }
    return result;
};
